import React from 'react';
import { Lead, Team } from '../types';
import { Button } from './ui/Button';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from './ui/Card';
import { Badge } from './ui/Badge';
import { ArrowLeft, ArrowRight, Users, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
interface TeamDistributionProps {
  leads: Lead[];
  teams: Team[];
  onAutoDistribute: (teamId: string) => void;
  onNext: () => void;
  onBack: () => void;
}
export function TeamDistribution({
  leads,
  teams,
  onAutoDistribute,
  onNext,
  onBack
}: TeamDistributionProps) {
  // Calculate stats for validation
  const totalAssignedToTeams = leads.filter(l => l.assignedTeamId).length;
  const totalAssignedToUsers = leads.filter(l => l.assignedUserId).length;
  const allDistributed = totalAssignedToTeams > 0 && totalAssignedToTeams === totalAssignedToUsers;
  return <div className="space-y-6" data-id="element-133">
      {/* Header */}
      <div className="flex justify-between items-center bg-white p-4 rounded-lg border border-slate-200 shadow-sm" data-id="element-134">
        <div data-id="element-135">
          <h2 className="text-lg font-semibold text-slate-900" data-id="element-136">
            Distribute to Users
          </h2>
          <p className="text-sm text-slate-500" data-id="element-137">
            Auto-distribute leads within each team to balance workload.
          </p>
        </div>
        <div className="flex items-center space-x-2" data-id="element-138">
          <span className="text-sm font-medium text-slate-600" data-id="element-139">
            Distribution Progress:
          </span>
          <div className="flex items-center" data-id="element-140">
            <span className={`text-lg font-bold ${allDistributed ? 'text-[#0D9488]' : 'text-slate-900'}`} data-id="element-141">
              {totalAssignedToUsers}
            </span>
            <span className="text-slate-400 mx-1" data-id="element-142">/</span>
            <span className="text-lg font-bold text-slate-400" data-id="element-143">
              {totalAssignedToTeams}
            </span>
          </div>
        </div>
      </div>

      {/* Team Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-144">
        {teams.map(team => {
        const teamLeads = leads.filter(l => l.assignedTeamId === team.id);
        const unassignedInTeam = teamLeads.filter(l => !l.assignedUserId).length;
        const assignedInTeam = teamLeads.length - unassignedInTeam;
        if (teamLeads.length === 0) return null;
        return <motion.div key={team.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3
        }} data-id="element-145">
              <Card className="h-full flex flex-col overflow-hidden border-t-4" style={{
            borderTopColor: team.color
          }} data-id="element-146">
                <CardHeader className="pb-2" data-id="element-147">
                  <div className="flex justify-between items-start" data-id="element-148">
                    <div data-id="element-149">
                      <CardTitle data-id="element-150">{team.name}</CardTitle>
                      <p className="text-xs text-slate-500 mt-1 font-medium" data-id="element-151">
                        Campaign: {team.campaignName}
                      </p>
                    </div>
                    <Badge color={team.color} data-id="element-152">{teamLeads.length} Leads</Badge>
                  </div>
                </CardHeader>

                <CardContent className="flex-1" data-id="element-153">
                  <div className="space-y-4" data-id="element-154">
                    <div className="flex justify-between items-center text-sm text-slate-600 mb-2" data-id="element-155">
                      <span data-id="element-156">Team Members</span>
                      <span data-id="element-157">Assigned</span>
                    </div>

                    <div className="space-y-3" data-id="element-158">
                      {team.users.map(user => {
                    const userLeadCount = teamLeads.filter(l => l.assignedUserId === user.id).length;
                    return <div key={user.id} className="flex items-center justify-between bg-slate-50 p-2 rounded-md" data-id="element-159">
                            <div className="flex items-center gap-3" data-id="element-160">
                              <div className="h-8 w-8 rounded-full bg-white border border-slate-200 flex items-center justify-center text-xs font-bold text-slate-700" data-id="element-161">
                                {user.avatarInitials}
                              </div>
                              <span className="text-sm font-medium text-slate-700" data-id="element-162">
                                {user.name}
                              </span>
                            </div>
                            <span className="text-sm font-bold text-slate-900" data-id="element-163">
                              {userLeadCount}
                            </span>
                          </div>;
                  })}
                    </div>
                  </div>
                </CardContent>

                <CardFooter className="bg-white border-t border-slate-100 pt-4" data-id="element-164">
                  <Button variant={unassignedInTeam === 0 ? 'outline' : 'primary'} className="w-full" onClick={() => onAutoDistribute(team.id)} disabled={unassignedInTeam === 0} leftIcon={unassignedInTeam > 0 ? <Zap className="h-4 w-4" data-id="element-166" /> : <Users className="h-4 w-4" data-id="element-167" />} data-id="element-165">
                    {unassignedInTeam === 0 ? 'Redistribute' : `Auto-Distribute (${unassignedInTeam} pending)`}
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>;
      })}
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t border-slate-200" data-id="element-168">
        <Button variant="outline" leftIcon={<ArrowLeft className="h-4 w-4" data-id="element-170" />} onClick={onBack} data-id="element-169">
          Back to Pool
        </Button>
        <Button variant="primary" size="lg" rightIcon={<ArrowRight className="h-4 w-4" data-id="element-172" />} onClick={onNext} disabled={!allDistributed} data-id="element-171">
          Next: Review
        </Button>
      </div>
    </div>;
}